import React from 'react';
import { Modal, Button } from '../../../components';

// The carer's day, mirroring their APP shift page: Visit (working), Break,
// Travel, Idle and the total shift window. Visit-driven — no clock in/out.
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—');
const fmtMins = (m) => {
  const v = Math.max(0, Math.round(m || 0));
  const h = Math.floor(v / 60);
  const min = v % 60;
  return h > 0 ? `${h}h ${min}m` : `${min}m`;
};

const CarerStatusModal = ({ open, carer, date, loading, data, onClose }) => {
  const initials = carer?.initials || '?';
  // Prefer the roster window returned by the API; fall back to the carer's band.
  const shiftStart = data?.shift_start || carer?.shift?.start || null;
  const shiftEnd = data?.shift_end || carer?.shift?.end || null;

  // Status pill mirrors the app: On Break / Travelling / Working / Idle.
  let label = 'Not started';
  let pill = 'bg-gray-100 text-gray-600 dark:bg-gray-900/40 dark:text-gray-300';
  let dot = 'bg-gray-400';
  let pulse = false;
  if (data) {
    const anyLogged = (data.working_minutes || 0) + (data.break_minutes || 0) + (data.travel_minutes || 0) > 0;
    if (data.on_break) { label = 'On Break'; pill = 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'; dot = 'bg-orange-500'; pulse = true; }
    else if (data.travelling) { label = 'Travelling'; pill = 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'; dot = 'bg-indigo-500'; pulse = true; }
    else if (data.visit_in_progress) { label = 'Working'; pill = 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'; dot = 'bg-emerald-500'; pulse = true; }
    else if (anyLogged) { label = 'Idle'; pill = 'bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-300'; dot = 'bg-slate-400'; }
  }

  const working = data?.working_minutes || 0;
  const visitCount = data?.visit_count || 0;
  const brk = data?.break_minutes || 0;
  const travel = data?.travel_minutes || 0;
  const idle = data?.idle_minutes || 0;
  const total = data?.total_minutes || 0;

  return (
    <Modal isOpen={open} onClose={onClose} title={carer ? `${carer.name} · Status` : 'Carer status'} maxWidth="max-w-lg">
      {loading ? (
        <div className="py-12 text-center text-sm text-gray-400 dark:text-gray-500">Loading…</div>
      ) : (
        <div className="space-y-5">
          {/* Header */}
          <div className="flex items-center gap-4 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-[#20242c] p-4">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-blue-100 text-sm font-bold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">{initials}</span>
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-[16px] font-bold text-gray-900 dark:text-gray-100">{carer?.name}</h3>
              <p className="text-[12px] text-gray-500 dark:text-gray-400">{date}{shiftStart && shiftEnd ? ` · Shift ${shiftStart}–${shiftEnd}` : ''}</p>
            </div>
            <span className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-tight ${pill}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${dot} ${pulse ? 'animate-pulse' : ''}`} />
              {label}
            </span>
          </div>

          {!data ? (
            <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-[#20242c] px-4 py-8 text-center text-[13px] text-gray-400 dark:text-gray-500">
              No activity for this carer on {date}.
            </div>
          ) : (
            <>
              {/* Total shift window + working hours headline */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-emerald-100 dark:border-emerald-900/40 bg-emerald-50/50 dark:bg-emerald-900/20 p-4">
                  <p className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-300">
                    Visit {data.visit_in_progress && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />}
                  </p>
                  <p className="text-[20px] font-black text-emerald-700 dark:text-emerald-300">{fmtMins(working)}</p>
                  <p className="text-[11px] text-emerald-600/70 dark:text-emerald-400/70">
                    working time · {visitCount} visit{visitCount === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="rounded-2xl border border-blue-100 dark:border-blue-900/40 bg-blue-50/50 dark:bg-blue-900/20 p-4">
                  <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-300">Total</p>
                  <p className="text-[20px] font-black text-blue-800 dark:text-blue-300">{fmtMins(total)}</p>
                  <p className="text-[11px] text-blue-600/70 dark:text-blue-400/70">shift window</p>
                </div>
              </div>

              {/* Break / Travel / Idle breakdown */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-2xl border border-orange-100 dark:border-orange-900/40 bg-orange-50/40 dark:bg-orange-900/20 p-4">
                  <p className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-orange-600 dark:text-orange-300">
                    Break {data.on_break && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-orange-500" />}
                  </p>
                  <p className="text-[16px] font-black text-orange-900 dark:text-orange-300">{fmtMins(brk)}</p>
                </div>
                <div className="rounded-2xl border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/40 dark:bg-indigo-900/20 p-4">
                  <p className="mb-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-300">
                    Travel {data.travelling && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-indigo-500" />}
                  </p>
                  <p className="text-[16px] font-black text-indigo-900 dark:text-indigo-300">{fmtMins(travel)}</p>
                </div>
                <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-[#31373f] p-4">
                  <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Idle</p>
                  <p className="text-[16px] font-black text-slate-700 dark:text-slate-200">{fmtMins(idle)}</p>
                </div>
              </div>

              {/* Active banner */}
              {(data.on_break || data.travelling) && (
                <div className={`flex items-center gap-3 rounded-xl border p-3 ${data.on_break ? 'border-orange-200 bg-orange-50 dark:border-orange-900/40 dark:bg-orange-900/20' : 'border-indigo-200 bg-indigo-50 dark:border-indigo-900/40 dark:bg-indigo-900/20'}`}>
                  <span className={`h-2 w-2 animate-pulse rounded-full ${data.on_break ? 'bg-orange-500' : 'bg-indigo-500'}`} />
                  <span className={`text-[12px] font-bold ${data.on_break ? 'text-orange-800 dark:text-orange-300' : 'text-indigo-800 dark:text-indigo-300'}`}>
                    {data.on_break ? `On break since ${fmtTime(data.break_started_at)}` : `Travelling since ${fmtTime(data.travel_started_at)}`}
                  </span>
                </div>
              )}
            </>
          )}

          <Button variant="primary" size="lg" block onClick={onClose}>Close</Button>
        </div>
      )}
    </Modal>
  );
};

export default CarerStatusModal;
